/**
 * Ethiopian Calendar Converter Utility
 * Converts Gregorian dates to Ethiopian calendar dates
 */

/**
 * Converts a Gregorian date to Ethiopian calendar date
 * @param {Date} gcDate - Gregorian calendar date
 * @returns {Object} - Ethiopian date with year, month, and day
 */
export const toEthiopianDate = (gcDate) => {
  const gcYear = gcDate.getFullYear();
  const gcMonth = gcDate.getMonth() + 1;
  const gcDay = gcDate.getDate();

  // Ethiopian New Year in Gregorian calendar
  const newYearDay = (gcYear % 4 === 3) ? 12 : 11;
  
  // Determine Ethiopian year
  let ecYear = gcYear - 7;
  if (gcMonth < 9 || (gcMonth === 9 && gcDay < newYearDay)) {
    ecYear -= 1;
  }

  // Calculate day of year in Gregorian calendar
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if ((gcYear % 4 === 0 && gcYear % 100 !== 0) || gcYear % 400 === 0) {
    daysInMonth[1] = 29; // Leap year
  }
  
  let gcDayOfYear = gcDay;
  for (let i = 0; i < gcMonth - 1; i++) {
    gcDayOfYear += daysInMonth[i];
  }

  // Ethiopian New Year day of year in Gregorian
  let ecNewYearDayOfYear = newYearDay;
  for (let i = 0; i < 8; i++) { // September is month 9, so sum up to August (index 7)
    ecNewYearDayOfYear += daysInMonth[i];
  }

  // Calculate difference
  let diff = gcDayOfYear - ecNewYearDayOfYear;
  if (diff < 0) {
    const prevYearDays = ((gcYear - 1) % 4 === 0 && (gcYear - 1) % 100 !== 0) || (gcYear - 1) % 400 === 0 ? 366 : 365;
    diff += prevYearDays;
  }

  // Convert to Ethiopian month and day
  const ecMonth = Math.floor(diff / 30) + 1;
  const ecDay = (diff % 30) + 1;

  return { ecYear, ecMonth, ecDay };
};

/**
 * Get the Ethiopian day of week for a given Gregorian date
 * @param {Date} gcDate - Gregorian calendar date
 * @returns {string} - Amharic day name
 */
export const getEthiopianDayOfWeek = (gcDate) => {
  // JavaScript getDay(): 0=Sunday, 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday
  const jsDay = gcDate.getDay();
  return amharicDays[jsDay];
};

/**
 * Ethiopian month names in Amharic
 */
export const amharicMonths = [
  'መስከረም', 'ጥቅምት', 'ህዳር', 'ታህሳስ', 'ጥር', 'የካቲት',
  'መጋቢት', 'ሚያዝያ', 'ግንቦት', 'ሰኔ', 'ሐምሌ', 'ነሐሴ', 'ጳጉሜ'
];

/**
 * Ethiopian month names in English (for JSON keys)
 */
export const englishMonths = [
  "Meskerem", "Tikimt", "Hidar", "Tahsas",
  "Tir", "Yekatit", "Megabit", "Miazia",
  "Ginbot", "Sene", "Hamle", "Nehasse", "Pagume"
];

/**
 * Amharic day names - Sunday to Saturday
 * Index 0 = Sunday (እሑድ), Index 1 = Monday (ሰኞ), etc.
 */
export const amharicDays = ['እሑድ', 'ሰኞ', 'ማክሰኞ', 'እሮብ', 'ኀሙስ', 'አርብ', 'ቅዳሜ'];

/**
 * Get Amharic month name by month number (1-13)
 * @param {number} monthNumber - Month number (1-13)
 * @returns {string} - Amharic month name
 */
export const getAmharicMonth = (monthNumber) => {
  if (monthNumber < 1 || monthNumber > 13) return "Invalid month";
  return amharicMonths[monthNumber - 1];
};

/**
 * Get English month name by month number (1-13)
 * @param {number} monthNumber - Month number (1-13)
 * @returns {string} - English month name
 */
export const getEnglishMonth = (monthNumber) => {
  if (monthNumber < 1 || monthNumber > 13) return "Invalid month";
  return englishMonths[monthNumber - 1];
};
